import React, { useEffect, useState } from "react";
import CreatableSelect from "react-select/creatable";
import { components } from "react-select";
import { Barn, Rider } from "../../models";
import Spinner from "../Spinners/Spinner";
import { IonCol } from "@ionic/react";
import ErrorAlert from "../Errors/ErrorAlert";
import VerifyRiderModal from "./VerifyRiderModal";
import { formattedOption } from "../../utilities/rider/SelectOrCreateRiderUtils";
const { v4: uuidv4 } = require("uuid");

interface SelectOrCreateRiderProps {
    userRiderOptions?: formattedOption[] | null | undefined;
    barnRiderOptions?: formattedOption[] | null | undefined;

    selectedBarn?: Barn | null | undefined;
    selectedValue?: string | null;
    onSelect: (rider?: Rider, riderName?: string) => void;
    isHeightRequired?: boolean;
    height?: string;
    disabledRiderId?: string | null | undefined;
    disabledRiderLabel?: string;
}

/**
 * This function is passed into the select component to define what should be shown when an option is selected
 * Instead of showing the 3 lines from the option,
 * it will only show the 1 line defined below: riderName
 * @param props
 * @returns
 */
const SingleValue = (props: any) => <components.SingleValue {...props}>{props.data.riderName || props.data.label}</components.SingleValue>;

const SelectOrCreateRider: React.FC<SelectOrCreateRiderProps> = ({
    userRiderOptions,
    barnRiderOptions,
    selectedBarn,
    selectedValue,
    onSelect,
    isHeightRequired,
    height,
    disabledRiderId,
    disabledRiderLabel = "Selected as Rider 1",
}) => {
    // Dropdown Section Titles
    const createdByUserGroupLabel = "Riders Created by You";
    const barnRidersGroupLabel = selectedBarn ? selectedBarn.name + " Riders" : "Barn Riders";

    // Groups - each group represents a section in the drop down
    const barnRidersGroup = {
        label: barnRidersGroupLabel,
        options:
            barnRiderOptions && barnRiderOptions.length > 0
                ? barnRiderOptions
                : [{ value: selectedBarn ? `No riders are in ${selectedBarn?.name}` : `No barn selected.`, label: " ", riderName: " " }],
    };

    const ridersCreatedByUserGroup = {
        label: createdByUserGroupLabel,
        options: userRiderOptions && userRiderOptions.length > 0 ? userRiderOptions : [{ value: "You have not created any riders yet.", label: "", riderName: "" }],
    };

    // General State
    const [currentValue, setCurrentValue] = useState<formattedOption | null | undefined>();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");

    const findValueInList = (value: string) => {
        // Set up the options for arrays of riders that should be checked
        const optionArray = (userRiderOptions || [])?.concat(barnRiderOptions || []);
        // If there are options, loop through them and set the current value
        if (optionArray) {
            for (var i = 0; i < optionArray.length; i++) {
                const currentOption = optionArray[i];
                if (currentOption.value === value) {
                    setCurrentValue(currentOption);
                }
            }
        }
    };

    if (selectedValue && !currentValue) {
        findValueInList(selectedValue);
    }

    useEffect(() => {
        if (selectedValue) {
            const optionArray = (userRiderOptions || []).concat(barnRiderOptions || []);
            const match = optionArray.find((o) => o.value === selectedValue);
            if (match) {
                setCurrentValue(match);
            }
        } else {
            setCurrentValue(null);
        }
    }, [selectedValue, userRiderOptions, barnRiderOptions]);

    // DROPDOWN Functions
    const handleOnChange = async (event?: any) => {
        // Rider was selected from either the barnRiders or ridersCreatedByUser groups
        if (event && event.rider) {
            await setCurrentValue(event);
            onSelect(event.rider);
        }

        // Option from the dropdown was created by the current user with only the rider name
        else if (event && event.riderName && event.riderName !== " ") {
            const newFormattedOption = handleCreateNewFormattedOptionWithJustRiderName(event.riderNameLabel);
            await setCurrentValue(newFormattedOption);
            onSelect(undefined, event.riderName);
        }

        // Option from the dropdown was that there are no riders in this section
        else if (event && event.riderName && event.riderName === " ") {
            // Open the modal and have the user type in a name and verify the rider.
            setIsModalVisible(true);
        }

        // Here - event.value will be the value the user typed into the CreateableSelect field
        else if (event && event.value) {
            // Set the currentValue in state so that it can be used inside of the modal.
            const newFormattedOption = handleCreateNewFormattedOptionWithJustRiderName(event.value);
            await setCurrentValue(newFormattedOption);

            // Verify Rider - If the rider was not in the barnRiders or ridersCreatedByUser groups
            setIsModalVisible(true);
        }

        // User clicked the X button to clear the value.
        else {
            await setCurrentValue(null);
            onSelect(undefined);
        }
    };

    const handleCreateNewFormattedOptionWithJustRiderName = (riderName: string) => {
        const newFormattedOption: formattedOption = {
            value: uuidv4(),
            label: riderName + " " + (selectedBarn?.name || ""),
            riderName: riderName,
            riderNameLabel: riderName,
            barnLabel: selectedBarn?.name || "",
            rider: undefined,
        };
        return newFormattedOption;
    };

    /**
     * User found the rider by name in the RSP Database
     * Send that rider info back to the parent component
     * And close the modal
     */
    const handleUseMatchedRider = async (event: formattedOption) => {
        if (event.riderName) {
            await setCurrentValue(event);
            onSelect(event.rider, event.riderName);
            setIsModalVisible(false);
        } else {
            setError("No rider name was found.");
        }
    };
    /**
     * User did not find the rider by name in the RSP Database
     * Send that rider name back to the parent component in order to create a new rider
     * And close the modal
     */
    const handleCreateNewRider = (newRiderName: string) => {
        onSelect(undefined, newRiderName);
        setIsModalVisible(false);
    };
    /**
     * User did not find the rider by name in the RSP Database.
     * Assume the user thought the rider existed and realized there is a typo in the rider name.
     * Send the user back to the starting point to try again.
     */
    const handleCancel = () => {
        onSelect(undefined);
        setIsModalVisible(false);
        setCurrentValue(null);
    };

    return (
        <>
            {isLoading ? (
                <Spinner />
            ) : (
                <IonCol size="10">
                    {error && <ErrorAlert error={error} width="12" />}
                    <CreatableSelect
                        id="select-or-create-rider"
                        inputId="select-or-create-rider-input"
                        placeholder="Type the rider's name"
                        styles={{
                            // Fixes the overlapping problem of the component
                            control: (baseStyles) => (isHeightRequired ? { ...baseStyles, height } : { ...baseStyles }),
                            menu: (provided) => ({ ...provided, zIndex: 9999 }),
                            group: (base, state) => ({
                                ...base,
                                // Group background color
                                backgroundColor: state.label === barnRidersGroupLabel ? "var(--ion-color-info)" : "var(--ion-color-secondary)",
                                padding: "10px",
                            }),
                            groupHeading: (base, state) => ({
                                ...base,
                                fontSize: "1em",
                                fontWeight: "bold",
                                // Section title color
                                color: "var(--ion-color-white)",
                            }),
                            option: (base, { isFocused, isSelected, isDisabled, data }) => {
                                const isRiderDisabled = disabledRiderId !== null && disabledRiderId !== undefined && data.value === disabledRiderId;
                                const disabled = isDisabled || isRiderDisabled;
                                return {
                                    ...base,
                                    // Hover vs. no hover colors for riders in list
                                    backgroundColor: disabled ? "var(--ion-color-light)" : isFocused || isSelected ? "var(--ion-color-light)" : "var(--ion-color-white)",
                                    cursor: disabled ? "not-allowed" : "pointer",
                                    color: disabled ? "var(--ion-color-medium)" : "var(--ion-color-primary)",
                                    opacity: disabled ? 0.7 : 1,
                                };
                            },
                        }}
                        defaultValue={currentValue}
                        value={currentValue}
                        menuPortalTarget={document.body}
                        menuPlacement={"auto"}
                        isClearable
                        options={[barnRidersGroup, ridersCreatedByUserGroup]}
                        noOptionsMessage={({ inputValue }) => (!inputValue ? "No input found" : "No results found")}
                        formatOptionLabel={(option: any) => {
                            const isRiderDisabled = disabledRiderId !== null && disabledRiderId !== undefined && option.value === disabledRiderId;
                            return option.riderNameLabel ? (
                                <div className="px-10">
                                    <div className="font-weight-bold text-default">{option.riderNameLabel}</div>
                                    {barnRidersGroup.options.includes(option) ? (
                                        // Display for barnRidersGroup
                                        <div className="display-5 text-medium">
                                            <div>Created By: {option.createdByLabel || "Unknown"}</div>
                                            <div>Rider Status: {option.riderStatusLabel || "Unknown"}</div>
                                        </div>
                                    ) : ridersCreatedByUserGroup.options.includes(option) ? (
                                        // Display for ridersCreatedByUserGroup
                                        <div className="display-5 text-medium">
                                            <div>Barn: {option.barnLabel || "Unknown"}</div>
                                            <div>Rider Status: {option.riderStatusLabel || "Unknown"}</div>
                                        </div>
                                    ) : null}
                                    {isRiderDisabled && <div className="text-danger font-weight-bold display-5">{disabledRiderLabel}</div>}
                                </div>
                            ) : (
                                <div className="p-0">
                                    <div className="font-weight-bold text-default">{option.textToDisplay || "Select or Create Rider: " + option.value}</div>
                                </div>
                            );
                        }}
                        components={{ SingleValue }}
                        onChange={(event: any) => handleOnChange(event)}
                        createOptionPosition={"first"}
                        isOptionDisabled={(option: any) => {
                            // Disable the option if it matches the disabledRiderId
                            return disabledRiderId !== null && disabledRiderId !== undefined && option.value === disabledRiderId;
                        }}
                    />
                </IonCol>
            )}
            {isModalVisible && (
                <VerifyRiderModal
                    currentValue={currentValue}
                    handleCancel={() => handleCancel()}
                    handleUseMatchedRider={(e: formattedOption) => handleUseMatchedRider(e)}
                    handleCreateNewRider={(newRiderName: string) => handleCreateNewRider(newRiderName)}
                />
            )}
        </>
    );
};

export default SelectOrCreateRider;
